import React, { useMemo } from "react";
import { useT, Namespace } from "@racoon/i18n";
import { Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import type { GpsStop } from "../model/useGpsStops";
import {
  GPS_STOP_TYPE_CONFIG,
  type GpsStopMarkerShape,
  type GpsStopType,
} from "../model/stop-types";

/** Marker size grows with dwell time so a 5-hour stop reads differently
 *  from a 40-second one without needing a click. */
function markerSizeFor(durationSeconds: number): number {
  if (durationSeconds >= 3600) return 22;
  if (durationSeconds >= 900) return 18;
  if (durationSeconds >= 300) return 15;
  return 12;
}

function shapeStyle(shape: GpsStopMarkerShape, color: string, size: number): string {
  const base = `width:${size}px;height:${size}px;box-sizing:border-box;`;
  switch (shape) {
    case "circle":
      return `${base}background:${color};border:2px solid #fff;border-radius:50%;`;
    case "diamond":
      return `${base}background:${color};border:2px solid #fff;transform:rotate(45deg);`;
    case "square":
      return `${base}background:${color};border:2px solid #fff;border-radius:2px;`;
    case "triangle":
      // A CSS triangle has no box to border, so the white outline comes from a
      // drop-shadow instead.
      return (
        `width:0;height:0;border-left:${size / 2}px solid transparent;` +
        `border-right:${size / 2}px solid transparent;` +
        `border-bottom:${size}px solid ${color};` +
        `filter:drop-shadow(0 0 1.5px #fff);`
      );
    case "hollow":
      return `${base}background:rgba(255,255,255,0.75);border:2px solid ${color};border-radius:50%;`;
  }
}

// Stable identity, not fewer allocations: react-leaflet diffs `icon` by
// reference and setIcon replaces the marker's element, stranding any open
// tooltip. Sharing one icon across markers is safe.
const gpsStopIconCache = new Map<string, L.DivIcon>();

function makeGpsStopIcon(type: GpsStopType, durationSeconds: number): L.DivIcon {
  const size = markerSizeFor(durationSeconds);
  const cacheKey = `${type}:${size}`;
  const cached = gpsStopIconCache.get(cacheKey);
  if (cached) return cached;

  const { color, shape } = GPS_STOP_TYPE_CONFIG[type];
  const icon = L.divIcon({
    className: "gps-stop-marker",
    html: `<div style="${shapeStyle(shape, color, size)}"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
  gpsStopIconCache.set(cacheKey, icon);
  return icon;
}

type DurationUnits = { s: string; m: string; h: string };

function formatDuration(seconds: number, u: DurationUnits): string {
  if (seconds < 60) return `${Math.round(seconds)} ${u.s}`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${u.m}`;
  const hours = Math.floor(minutes / 60);
  return `${hours} ${u.h} ${minutes % 60} ${u.m}`;
}

const timeFormatter = new Intl.DateTimeFormat("ru-RU", {
  hour: "2-digit",
  minute: "2-digit",
});

export interface GpsStopsLayerProps {
  stops: GpsStop[];
  /** Types currently checked in the legend. */
  visibleTypes: Set<GpsStopType>;
}

/**
 * Renders GPS dwell events as markers. Must be mounted inside a
 * react-leaflet MapContainer.
 */
export const GpsStopsLayer: React.FC<GpsStopsLayerProps> = ({ stops, visibleTypes }) => {
  const t = useT();
  const units: DurationUnits = {
    s: t(Namespace.GpsTracking, "gpsStopUnitSeconds"),
    m: t(Namespace.GpsTracking, "gpsStopUnitMinutes"),
    h: t(Namespace.GpsTracking, "gpsStopUnitHours"),
  };

  const visible = useMemo(
    () =>
      // Longest dwells render first so the small, short stops end up on top and
      // stay clickable.
      stops
        .filter((s) => visibleTypes.has(s.type))
        .slice()
        .sort((a, b) => b.durationSeconds - a.durationSeconds),
    [stops, visibleTypes],
  );

  return (
    <>
      {visible.map((stop) => {
        const config = GPS_STOP_TYPE_CONFIG[stop.type];
        return (
          <Marker
            key={`gps-stop-${stop.id}`}
            position={[stop.lat, stop.lon]}
            icon={makeGpsStopIcon(stop.type, stop.durationSeconds)}
            zIndexOffset={500}
          >
            <Tooltip direction="top" offset={[0, -8]}>
              <div className="text-xs leading-snug">
                <div className="font-medium" style={{ color: config.color }}>
                  {t(Namespace.GpsTracking, config.labelKey)}
                </div>
                {stop.siteName && <div>{stop.siteName}</div>}
                <div>
                  {timeFormatter.format(stop.startAt)}–{timeFormatter.format(stop.endAt)} (
                  {formatDuration(stop.durationSeconds, units)})
                </div>
                {/* job_stop_detector leaves num_gps_points at 0 on every row it
                    writes, so the count is only meaningful when it is non-zero. */}
                <div className="text-text-secondary">
                  {stop.numGpsPoints > 0 &&
                    `${stop.numGpsPoints} ${t(Namespace.GpsTracking, "gpsStopPoints")}, `}
                  {t(Namespace.GpsTracking, "gpsStopAvgSpeed")} {stop.averageSpeedKmph.toFixed(1)}{" "}
                  {t(Namespace.GpsTracking, "gpsStopSpeedUnit")}
                </div>
              </div>
            </Tooltip>
          </Marker>
        );
      })}
    </>
  );
};
